import {
  AGENT_IDS,
  ENRICHMENT_METADATA_TYPES,
  EnrichmentFieldValue,
  EnrichmentToolId,
} from '@/app/data-catalog/_common/constants'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'

export const ENRICHMENT_ERROR = 'FAILED_TO_ENRICH_METADATA'

export type EnrichMetadataParams = {
  service_name: string
  database_name: string
  schema_name: string
  industry?: string
  tables: string[]
  enrichment_fields: EnrichmentFieldValue[]
  tool_id: EnrichmentToolId
}

export type WorkflowResponse = {
  workflowId: string
  turnId: string
}

export function useEnrichMetadataMutation() {
  const agentId = AGENT_IDS.METADATA_AGENT
  const { apiRequest, domainId } = useApiRequest()

  return useMutation<WorkflowResponse, Error, EnrichMetadataParams>({
    mutationFn: async (params: EnrichMetadataParams) => {
      const basePath = taskManagerApiPaths.workflows({ domainId })

      // Create FormData to match the curl multipart/form-data format
      const formData = new FormData()

      // Add the JSON data as a form field named 'data'
      const jsonData = {
        content: {
          payload: {
            service_name: params.service_name,
            database_name: params.database_name,
            schema_name: params.schema_name,
            // Only include industry if the first enrichment field is not 'dq_rules'
            ...(params.enrichment_fields[0] !== ENRICHMENT_METADATA_TYPES.DQ_RULES && {
              industry: params.industry || '',
            }),
            tables: params.tables,
            enrichment_fields: params.enrichment_fields,
            reference_document_identifiers: [],
          },
        },
      }

      formData.append('data', JSON.stringify(jsonData))

      const response = await apiRequest({
        path: basePath,
        queryParams: {
          agent_id: agentId,
          tool_id: params.tool_id,
        },
        type: 'MULTIPART',
        options: {
          method: 'POST',
          body: formData,
        },
      })

      if (!response.ok) {
        throw new Error(ENRICHMENT_ERROR)
      }

      const json = (await response.json()) as WorkflowResponse
      return json
    },
  })
}
